// TripList — the in-store cart-and-list pane below the scanner.
//
// SCAN PAGE ARCHITECTURE (load-bearing per user 2026-05-03):
//   The scan page is JUST scan + pair + confirmation. NO resolution
//   UI here. The user said the loud part: "why are we resolving the
//   fucking items on the scan page" — resolution belongs in the
//   Resolve phase, after they're done shopping. Editing canonicals,
//   brands, sizes, locations all happens there, not here.
//
// What this component does:
//   1. A SEARCH input filters the shopping list — they have many
//      items and want to scroll/filter to find what to pair next.
//   2. The (filtered) shopping list shows tap-to-arm rows.
//   3. The cart section shows the most recent scans as compact rows
//      with tier color + name + qty + paired-list-item label. ONE
//      LINE each. No chips, no pickers, no "edit anything" UI.
//   4. Done shopping → Resolve phase (handled by parent via the CTA).

import { useEffect, useMemo, useState } from "react";
import {
  useShopTrip,
  tripActions,
  selectScansArray,
} from "../../lib/useShopTrip";
import { TIER_COLORS } from "./tierColors";
import { COLOR, FONT, RADIUS, SPACE } from "../../lib/tokens";

const sectionTitle = {
  fontFamily: FONT.mono,
  fontSize: 10,
  letterSpacing: "0.18em",
  textTransform: "uppercase",
  color: COLOR.muted,
  marginBottom: 8,
};

const searchInputStyle = {
  width: "100%",
  padding: "10px 12px",
  fontFamily: FONT.forma,
  fontSize: 14,
  color: COLOR.ink,
  background: COLOR.ground,
  border: `1px solid ${COLOR.edge}`,
  borderRadius: RADIUS.md,
  outline: "none",
  marginBottom: 10,
};

const armRowStyle = (armed) => ({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  gap: 2,
  padding: "8px 12px",
  background: armed ? COLOR.goldDeep : COLOR.ground,
  border: `1px solid ${armed ? COLOR.gold : COLOR.edge}`,
  borderRadius: RADIUS.md,
  textAlign: "left",
  cursor: "pointer",
});

const armRemoveBtn = {
  position: "absolute",
  top: 4,
  right: 4,
  width: 28,
  height: 28,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontFamily: FONT.mono,
  fontSize: 18,
  lineHeight: 1,
  color: COLOR.ink,
  background: COLOR.ground,
  border: `1px solid ${COLOR.edge}`,
  borderRadius: "50%",
  padding: 0,
  cursor: "pointer",
  touchAction: "manipulation",
  zIndex: 1,
};

// "Add to cart without scanning" button — for produce, bakery, bulk
// bins, anything without a barcode. Sits to the LEFT of the remove
// (×) button so the two stay visually distinct. Green to read as
// "advance / commit" not "destroy."
const armAddBtn = {
  position: "absolute",
  top: 4,
  right: 38,
  height: 28,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontFamily: FONT.mono,
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: "0.14em",
  lineHeight: 1,
  color: "#FFFFFF",
  background: COLOR.leaf,
  border: "none",
  borderRadius: 14,
  padding: "0 12px",
  cursor: "pointer",
  touchAction: "manipulation",
  zIndex: 1,
};

const cartRowStyle = (tone) => ({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  gap: 2,
  padding: "8px 12px",
  background: COLOR.ground,
  border: `1px solid ${tone.border}`,
  borderLeft: `4px solid ${tone.strip}`,
  borderRadius: RADIUS.md,
  textAlign: "left",
});

const cartTopLine = {
  display: "flex",
  alignItems: "baseline",
  gap: 8,
};

const cartName = {
  flex: 1,
  minWidth: 0,
  fontFamily: FONT.itemTitle,
  fontStyle: "italic",
  fontWeight: 500,
  fontSize: 16,
  color: COLOR.ink,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const cartSub = {
  fontFamily: FONT.forma,
  fontSize: 11,
  color: COLOR.muted,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const cartQty = {
  fontFamily: FONT.mono,
  fontSize: 11,
  letterSpacing: 0.4,
  color: COLOR.muted,
  flex: "0 0 auto",
};

const removeBtn = {
  position: "absolute",
  top: 4,
  right: 4,
  width: 28,
  height: 28,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontFamily: FONT.mono,
  fontSize: 18,
  lineHeight: 1,
  color: COLOR.ink,
  background: COLOR.ground,
  border: `1px solid ${COLOR.edge}`,
  borderRadius: "50%",
  padding: 0,
  cursor: "pointer",
  touchAction: "manipulation",
};

export default function TripList({ shoppingList = [], onRemoveShoppingItem }) {
  const scans = useShopTrip(selectScansArray);
  const armedListId = useShopTrip((s) => s.armedListId);
  const placementOverrides = useShopTrip((s) => s.placementOverrides);
  const [search, setSearch] = useState("");
  // Cart-row inline editor — at most one row is expanded at a time.
  // Tapping the same row again collapses it; tapping a different row
  // moves the expansion. Stored as scanId | null.
  const [editingScanId, setEditingScanId] = useState(null);
  const [editName, setEditName] = useState("");

  // Sync shopping list rows into the trip store so handleDetected can
  // resolve pair-anchoring without prop drilling.
  useEffect(() => {
    tripActions.setListTargets(shoppingList || []);
  }, [shoppingList]);

  const pairedListIds = useMemo(() => {
    const set = new Set();
    for (const s of scans) if (s.pairedShoppingListItemId) set.add(s.pairedShoppingListItemId);
    return set;
  }, [scans]);

  const unpurchasedTargets = useMemo(() => {
    return (shoppingList || []).filter(
      (it) => !it.purchasedAt && !pairedListIds.has(it.id)
    );
  }, [shoppingList, pairedListIds]);

  const filteredTargets = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return unpurchasedTargets;
    return unpurchasedTargets.filter((it) =>
      String(it.name || "").toLowerCase().includes(q) ||
      String(it.category || "").toLowerCase().includes(q)
    );
  }, [unpurchasedTargets, search]);

  // Cart renders newest-first so the just-scanned row is at the top.
  const cartScans = useMemo(() => [...scans].reverse(), [scans]);

  return (
    <div style={{
      display: "flex",
      flexDirection: "column",
      gap: SPACE.section,
      padding: "14px 18px 20px 18px",
      overflowY: "auto",
      height: "100%",
    }}>
      {/* CART — newest scans on top. ONE LINE per row. */}
      <div>
        <div style={sectionTitle}>
          {scans.length === 0 ? "Cart" : `Cart · ${scans.length} item${scans.length === 1 ? "" : "s"}`}
        </div>
        {scans.length === 0 ? (
          <div style={{
            padding: "12px 14px",
            fontFamily: FONT.forma,
            fontStyle: "italic",
            fontSize: 13,
            color: COLOR.muted,
            textAlign: "center",
          }}>
            Aim at a barcode to start filling the cart.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {cartScans.map((scan) => {
              const tone = TIER_COLORS[scan.status] || TIER_COLORS.red;
              const listItem = scan.pairedShoppingListItemId
                ? (shoppingList || []).find((it) => it.id === scan.pairedShoppingListItemId)
                : null;
              const display =
                scan.displayName ||
                scan.productName ||
                scan.brand ||
                "(needs name)";
              const subBits = [];
              subBits.push(tone.label);
              if (listItem?.name) subBits.push(`→ ${listItem.name}`);
              if (scan.brand) subBits.push(scan.brand);
              const isEditing = editingScanId === scan.id;
              const openEdit = () => {
                if (isEditing) {
                  setEditingScanId(null);
                } else {
                  setEditingScanId(scan.id);
                  setEditName(display === "(needs name)" ? "" : display);
                }
              };
              const saveName = () => {
                const next = (editName || "").trim();
                if (next && next !== display) {
                  tripActions.applyScanPatch(scan.id, { product_name: next });
                }
                setEditingScanId(null);
              };
              const bumpQty = (delta) => {
                tripActions.adjustQty(scan.id, Math.max(1, (scan.qty || 1) + delta));
              };
              const repairTo = (listItemId) => {
                if (listItemId) tripActions.pairScan(scan.id, listItemId);
                else tripActions.unpairScan(scan.id);
              };
              const addClaim = (raw) => {
                const v = String(raw || "").trim().toLowerCase();
                if (!v) return;
                const existing = Array.isArray(scan.claims) ? scan.claims : [];
                if (existing.includes(v)) return;
                tripActions.applyScanPatch(scan.id, { claims: [...existing, v] });
              };
              const removeClaim = (claim) => {
                const existing = Array.isArray(scan.claims) ? scan.claims : [];
                tripActions.applyScanPatch(scan.id, { claims: existing.filter((c) => c !== claim) });
              };
              const setBrand = (raw) => {
                const v = String(raw || "").trim();
                tripActions.applyScanPatch(scan.id, { brand: v || null });
              };
              const setSize = (amount, unit) => {
                tripActions.setPlacementOverride(scan.id, {
                  packageAmount: amount,
                  packageUnit: unit,
                });
              };
              const unpairedTargets = (shoppingList || []).filter(
                (it) => !it.purchasedAt && it.id !== scan.pairedShoppingListItemId
                  && !cartScans.some((sc) => sc.id !== scan.id && sc.pairedShoppingListItemId === it.id)
              );
              return (
                <div key={scan.id} style={cartRowStyle(tone)}>
                  <button
                    onClick={() => tripActions.deleteScan(scan.id)}
                    style={removeBtn}
                    aria-label="Remove from cart"
                    className="mcm-focusable"
                  >
                    ×
                  </button>
                  {/* Tap-to-expand the row body. The whole top area
                      is one button so the user can hit anywhere
                      except the × to enter edit mode. */}
                  <button
                    onClick={openEdit}
                    style={{
                      display: "block", width: "100%", textAlign: "left",
                      background: "transparent", border: "none", padding: 0,
                      cursor: "pointer", color: "inherit",
                    }}
                    aria-expanded={isEditing}
                    aria-label="Edit this cart item"
                  >
                    <div style={{ ...cartTopLine, paddingRight: 32 }}>
                      <div style={{
                        ...cartName,
                        // Paired = canonical is bound. Green names read as
                        // "we know what this is"; rose names flag rows that
                        // still need a canonical pick in the Resolve phase.
                        color: scan.canonicalId ? COLOR.leaf : COLOR.rose,
                      }}>{display}</div>
                      {scan.qty > 1 && <div style={cartQty}>×{scan.qty}</div>}
                    </div>
                    <div style={cartSub}>
                      {subBits.join(" · ")}
                      {!isEditing && <span style={{ marginLeft: 6, opacity: 0.6 }}>· tap to edit</span>}
                    </div>
                  </button>

                  {isEditing && (
                    <div style={{
                      marginTop: 10, paddingTop: 10,
                      borderTop: `1px dashed ${COLOR.edge}`,
                      display: "flex", flexDirection: "column", gap: 10,
                    }}>
                      {/* Name editor */}
                      <div>
                        <div style={{ fontFamily: FONT.mono, fontSize: 9, letterSpacing: "0.18em", color: COLOR.muted, marginBottom: 4 }}>
                          NAME
                        </div>
                        <input
                          type="text"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveName(); } }}
                          placeholder="What is this?"
                          style={{
                            width: "100%", padding: "8px 10px",
                            fontFamily: FONT.detail, fontStyle: "italic", fontSize: 15,
                            color: COLOR.ink, background: COLOR.ground,
                            border: `1px solid ${COLOR.edge}`, borderRadius: RADIUS.md,
                            outline: "none",
                          }}
                        />
                      </div>

                      {/* Brand input — required for green tier. Empty
                          brand keeps the row yellow even with canonical
                          + size set (per classifyScan rule). */}
                      <div>
                        <div style={{ fontFamily: FONT.mono, fontSize: 9, letterSpacing: "0.18em", color: COLOR.muted, marginBottom: 4 }}>
                          BRAND
                        </div>
                        <input
                          type="text"
                          defaultValue={scan.brand || ""}
                          onBlur={(e) => setBrand(e.currentTarget.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              setBrand(e.currentTarget.value);
                              e.currentTarget.blur();
                            }
                          }}
                          placeholder="e.g. Swanson, Kerrygold…"
                          style={{
                            width: "100%", padding: "8px 10px",
                            fontFamily: FONT.detail, fontStyle: "italic", fontSize: 14,
                            color: COLOR.ink, background: COLOR.ground,
                            border: `1px solid ${COLOR.edge}`, borderRadius: RADIUS.md,
                            outline: "none",
                          }}
                        />
                      </div>

                      {/* Package size — number + unit. Stored on the
                          per-scan placementOverride map; classifyScan
                          reads it via the synthetic off_payload at
                          reclassify time and flips the row green when
                          combined with canonical + brand. */}
                      {(() => {
                        const override = placementOverrides?.get?.(scan.id) || {};
                        const curAmt = override.packageAmount ?? "";
                        const curUnit = override.packageUnit ?? "";
                        return (
                          <div>
                            <div style={{ fontFamily: FONT.mono, fontSize: 9, letterSpacing: "0.18em", color: COLOR.muted, marginBottom: 4 }}>
                              SIZE
                            </div>
                            <div style={{ display: "flex", gap: 6 }}>
                              <input
                                type="number"
                                inputMode="decimal"
                                defaultValue={curAmt}
                                onBlur={(e) => {
                                  const n = parseFloat(e.currentTarget.value);
                                  if (Number.isFinite(n) && n > 0 && curUnit) {
                                    setSize(n, curUnit);
                                  }
                                }}
                                placeholder="amt"
                                style={{
                                  flex: 1, padding: "8px 10px",
                                  fontFamily: FONT.detail, fontStyle: "italic", fontSize: 14,
                                  color: COLOR.ink, background: COLOR.ground,
                                  border: `1px solid ${COLOR.edge}`, borderRadius: RADIUS.md,
                                  outline: "none",
                                }}
                              />
                              <select
                                defaultValue={curUnit || "oz"}
                                onChange={(e) => {
                                  const unit = e.currentTarget.value;
                                  if (curAmt) setSize(curAmt, unit);
                                }}
                                style={{
                                  flex: 1, padding: "8px 10px",
                                  fontFamily: FONT.mono, fontSize: 12,
                                  color: COLOR.ink, background: COLOR.ground,
                                  border: `1px solid ${COLOR.edge}`, borderRadius: RADIUS.md,
                                  outline: "none",
                                }}
                              >
                                <option value="oz">oz</option>
                                <option value="lb">lb</option>
                                <option value="g">g</option>
                                <option value="kg">kg</option>
                                <option value="ml">ml</option>
                                <option value="L">L</option>
                                <option value="fl oz">fl oz</option>
                                <option value="count">count</option>
                                <option value="package">package</option>
                              </select>
                            </div>
                          </div>
                        );
                      })()}

                      {/* Qty stepper */}
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div style={{ fontFamily: FONT.mono, fontSize: 9, letterSpacing: "0.18em", color: COLOR.muted, flex: 1 }}>
                          QTY
                        </div>
                        <button
                          onClick={() => bumpQty(-1)}
                          disabled={(scan.qty || 1) <= 1}
                          style={{
                            width: 32, height: 32, borderRadius: 8,
                            background: COLOR.ground, color: COLOR.ink,
                            border: `1px solid ${COLOR.edge}`,
                            fontFamily: FONT.mono, fontSize: 16, fontWeight: 600,
                            cursor: (scan.qty || 1) <= 1 ? "not-allowed" : "pointer",
                            opacity: (scan.qty || 1) <= 1 ? 0.4 : 1,
                          }}
                        >−</button>
                        <div style={{
                          minWidth: 32, textAlign: "center",
                          fontFamily: FONT.mono, fontSize: 14, fontWeight: 600, color: COLOR.ink,
                        }}>
                          {scan.qty || 1}
                        </div>
                        <button
                          onClick={() => bumpQty(1)}
                          style={{
                            width: 32, height: 32, borderRadius: 8,
                            background: COLOR.ground, color: COLOR.ink,
                            border: `1px solid ${COLOR.edge}`,
                            fontFamily: FONT.mono, fontSize: 16, fontWeight: 600,
                            cursor: "pointer",
                          }}
                        >+</button>
                      </div>

                      {/* Variants / claims — modifier tokens like "spicy",
                          "sharp", "white", "smoked", "low sodium". Stored
                          as an array on the scan; pass through to the
                          pantry row on commit so they survive into the
                          kitchen and are searchable. */}
                      <div>
                        <div style={{ fontFamily: FONT.mono, fontSize: 9, letterSpacing: "0.18em", color: COLOR.muted, marginBottom: 6 }}>
                          VARIANTS
                        </div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 6 }}>
                          {(scan.claims || []).map((claim) => (
                            <button
                              key={claim}
                              onClick={() => removeClaim(claim)}
                              style={{
                                padding: "5px 10px",
                                background: TIER_COLORS.green.bg,
                                border: `1px solid ${TIER_COLORS.green.border}`,
                                borderRadius: 999,
                                fontFamily: FONT.detail, fontStyle: "italic", fontSize: 12,
                                color: COLOR.ink,
                                cursor: "pointer",
                                display: "inline-flex", alignItems: "center", gap: 5,
                              }}
                              aria-label={`Remove variant ${claim}`}
                            >
                              {claim}
                              <span style={{ opacity: 0.5, fontSize: 13, lineHeight: 1 }}>×</span>
                            </button>
                          ))}
                        </div>
                        <input
                          type="text"
                          placeholder="add variant — spicy, sharp, organic, low sodium…"
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              addClaim(e.currentTarget.value);
                              e.currentTarget.value = "";
                            }
                          }}
                          style={{
                            width: "100%", padding: "8px 10px",
                            fontFamily: FONT.detail, fontStyle: "italic", fontSize: 14,
                            color: COLOR.ink, background: COLOR.ground,
                            border: `1px solid ${COLOR.edge}`, borderRadius: RADIUS.md,
                            outline: "none",
                          }}
                        />
                      </div>

                      {/* Pairing — what shopping list slot this fills */}
                      <div>
                        <div style={{ fontFamily: FONT.mono, fontSize: 9, letterSpacing: "0.18em", color: COLOR.muted, marginBottom: 6 }}>
                          PAIRED TO
                        </div>
                        {listItem ? (
                          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                            <div style={{
                              padding: "5px 10px",
                              background: TIER_COLORS.green.bg,
                              border: `1px solid ${TIER_COLORS.green.border}`,
                              borderRadius: RADIUS.pill || 999,
                              fontFamily: FONT.detail, fontStyle: "italic", fontSize: 13,
                              color: COLOR.ink,
                            }}>
                              {listItem.name}
                            </div>
                            <button
                              onClick={() => repairTo(null)}
                              style={{
                                background: "transparent",
                                border: `1px solid ${COLOR.edge}`,
                                color: COLOR.muted,
                                padding: "5px 10px",
                                borderRadius: RADIUS.pill || 999,
                                fontFamily: FONT.mono, fontSize: 9, letterSpacing: "0.14em",
                                cursor: "pointer",
                              }}
                            >
                              UNPAIR
                            </button>
                          </div>
                        ) : (
                          <div style={{
                            fontFamily: FONT.forma, fontSize: 12, fontStyle: "italic",
                            color: COLOR.muted, marginBottom: 6,
                          }}>
                            Not paired to any shopping list item.
                          </div>
                        )}
                        {unpairedTargets.length > 0 && (
                          <div style={{ marginTop: 6 }}>
                            <div style={{ fontFamily: FONT.mono, fontSize: 9, letterSpacing: "0.16em", color: COLOR.muted, marginBottom: 4 }}>
                              {listItem ? "OR PAIR TO" : "PAIR TO"}
                            </div>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                              {unpairedTargets.map((it) => (
                                <button
                                  key={it.id}
                                  onClick={() => repairTo(it.id)}
                                  style={{
                                    padding: "5px 10px",
                                    background: COLOR.ground,
                                    border: `1px solid ${COLOR.edge}`,
                                    borderRadius: RADIUS.pill || 999,
                                    fontFamily: FONT.detail, fontStyle: "italic", fontSize: 12,
                                    color: COLOR.ink,
                                    cursor: "pointer",
                                  }}
                                >
                                  {it.name}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Save / Done — applies pending name change. The
                          qty + pair changes already commit on tap, so
                          this button is essentially "save name + close." */}
                      <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                        <button
                          onClick={saveName}
                          style={{
                            flex: 1, padding: "10px 14px",
                            background: COLOR.leaf, color: COLOR.night,
                            border: "none", borderRadius: RADIUS.md,
                            fontFamily: FONT.mono, fontSize: 11, fontWeight: 600, letterSpacing: "0.16em",
                            cursor: "pointer",
                          }}
                        >
                          DONE
                        </button>
                        <button
                          onClick={() => setEditingScanId(null)}
                          style={{
                            padding: "10px 14px",
                            background: "transparent", color: COLOR.muted,
                            border: `1px solid ${COLOR.edge}`, borderRadius: RADIUS.md,
                            fontFamily: FONT.mono, fontSize: 11, letterSpacing: "0.16em",
                            cursor: "pointer",
                          }}
                        >
                          CANCEL
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Shopping list — tap to arm. Search filters by name + category
          since the list grows long. */}
      {unpurchasedTargets.length > 0 && (
        <div>
          <div style={sectionTitle}>Shopping list — tap to arm</div>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={`Search ${unpurchasedTargets.length} list items…`}
            style={searchInputStyle}
          />
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {filteredTargets.length === 0 ? (
              <div style={{
                padding: "10px 12px",
                fontFamily: FONT.forma,
                fontStyle: "italic",
                fontSize: 12,
                color: COLOR.muted,
                textAlign: "center",
              }}>
                No matches.
              </div>
            ) : (
              filteredTargets.map((item) => {
                const armed = armedListId === item.id;
                return (
                  <div key={item.id} style={{ position: "relative" }}>
                    <button
                      onClick={() =>
                        tripActions.armListItem(armed ? null : item.id)
                      }
                      className="mcm-focusable"
                      style={{ ...armRowStyle(armed), width: "100%", paddingRight: onRemoveShoppingItem ? 110 : 78 }}
                    >
                      <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                        <div style={{
                          flex: 1,
                          minWidth: 0,
                          fontFamily: FONT.itemTitle,
                          fontStyle: "italic",
                          fontWeight: 500,
                          fontSize: 15,
                          color: COLOR.ink,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}>
                          {item.name || "(unnamed)"}
                        </div>
                        {item.amount && (
                          <div style={{
                            fontFamily: FONT.mono,
                            fontSize: 10,
                            letterSpacing: 0.3,
                            color: COLOR.muted,
                          }}>
                            {item.amount} {item.unit || ""}
                          </div>
                        )}
                      </div>
                      <div style={{
                        fontFamily: FONT.forma,
                        fontSize: 10,
                        color: COLOR.muted,
                      }}>
                        {armed ? "armed — next scan pairs here" : "tap to arm"}
                        {item.category ? ` · ${item.category}` : ""}
                      </div>
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        tripActions.placeListItem(item);
                      }}
                      style={{
                        ...armAddBtn,
                        // Push further right when there's no remove
                        // button to share the corner with.
                        right: onRemoveShoppingItem ? 38 : 4,
                      }}
                      aria-label={`Add ${item.name || "item"} to cart without scanning`}
                      className="mcm-focusable"
                    >
                      ✓ ADD
                    </button>
                    {onRemoveShoppingItem && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onRemoveShoppingItem(item.id);
                        }}
                        style={armRemoveBtn}
                        aria-label={`Remove ${item.name || "item"} from list`}
                        className="mcm-focusable"
                      >
                        ×
                      </button>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
